import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import ApiPaas from '@/api/ApiPaas';
import { datetimeToString } from '@/components/common/dateUtils';
import { useUserStore } from '@/stores/user';
import { countPaasListFilter } from '@/components/paas/paasListStatusUtils';

import type {
	PaasCoordRaw,
	PaasStatusResponse,
	PaasTaskErrorRaw,
	PaasTaskProblemRaw,
	PaasTaskRaw,
	PaasTaskStationRaw,
	PaasTaskStored,
} from '@/api/ApiTypes';


const addPaasStatusData = (item: PaasTaskRaw): PaasTaskStored => ({
	...item,
	createdStr: datetimeToString(item.created),
});


export const usePaasStore = defineStore('paas', () => {
	const userStore = useUserStore();

	const isLoading = ref(true);
	const newTask = ref(false);
	const allowToStart = ref(false);
	const showProgress = ref(false);
	const lastStart = ref<string | null>(null);
	const finishedCount = ref(0);
	const taskErrors = ref<PaasTaskErrorRaw[]>([]);
	const taskProblems = ref<PaasTaskProblemRaw[]>([]);
	const taskStations = ref<PaasTaskStationRaw[]>([]);
	const paasTasks = ref<PaasTaskStored[]>([]);
	const paasCoords = ref<PaasCoordRaw[]>([]);
	const mountpoints = ref<string[]>([]);

	const paasStatusGetHandler = (response: PaasStatusResponse): void => {
		isLoading.value = false;
		newTask.value = response.new_task;
		allowToStart.value = response.current_task.allow_to_start;
		showProgress.value = response.current_task.show_progress;
		lastStart.value = response.current_task.last_start
			? datetimeToString(response.current_task.last_start)
			: null;
		finishedCount.value = response.current_task.finished_count;
		taskErrors.value = response.current_task.errors;
		taskProblems.value = response.current_task.problems;
		taskStations.value = response.current_task.stations ?? [];
		paasTasks.value = response.previous_tasks.map(addPaasStatusData);
		paasCoords.value = response.coords;
		mountpoints.value = response.mountpoints;
	};

	const updatePaasData = (): void => {
		if (userStore.allowToSeePaas) {
			isLoading.value = true;
			ApiPaas.paasStatusGet(paasStatusGetHandler);
		}
	};

	return {
		isLoading,
		newTask,
		allowToStart,
		showProgress,
		lastStart,
		finishedCount,
		totalCount: computed(() => countPaasListFilter(taskStations.value, 'sentTotal')),
		solutionCount: computed(() => countPaasListFilter(taskStations.value, 'solutions')),
		sentWithMeasurementsCount: computed(() => countPaasListFilter(taskStations.value, 'sentWithMeasurements')),
		errorsCount: computed(() => countPaasListFilter(taskStations.value, 'errors')),
		taskErrors,
		taskProblems,
		taskStations,
		paasTasks,
		paasCoords,
		mountpoints,
		updatePaasData,
	};
});
